import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const Privacy = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <article className="container">
            <div className="page mb-2">
                <h2>Privacy Notice</h2>
                <p><em>Last updated: 18 July 2026</em></p>

                <h3>Contact Information</h3>
    <p><strong>Name:</strong> Grassroots360 LTD</p>
    <p><strong>Address:</strong> 11 Broadstone Close, Great Sankey, Warrington, Cheshire, WA5 3YS</p>
    <p><strong>Email:</strong> <a href="mailto:privacy@grassroots360.co.uk">privacy@grassroots360.co.uk</a></p>

    <h3>Who This Notice Covers</h3>
    <p>
        This notice covers the Grassroots360 website, web platform and mobile app. Grassroots360 provides
        club-management services to grassroots sports clubs; the app is intended for use by adults —
        parents, carers, coaches and club officials. Children do not use the app themselves, but
        information about child players is entered by their parents, carers or club officials.
    </p>

    <h3>The Type of Personal Information We Collect</h3>
    <p>We currently collect and process the following personal information:</p>
    <ul>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Account and profile details</strong> — name, date of birth, email address, phone number, postal address, and membership/fan number</li>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Player (child) details</strong> — name, date of birth, home address, team membership, and an optional profile photograph</li>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Health information about players</strong> — medical history, medication requirements and dietary requirements, provided with the consent of a parent or carer</li>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Accident and injury records</strong> — where an accident occurs at a club activity: the injured person's details (including date of birth for under-18s), the nature of the injury, first aid given, hospital attendance, and witness details. These are statutory accident-book (BI510/RIDDOR) records</li>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Parent/carer contacts</strong> — names and email addresses of the adults linked to a player</li>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Payment records</strong> — subscription, sign-on fee, invoice and ticket records. Card payments are handled by Stripe on their secure pages; we never see or store your card number</li>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Bank details for volunteers</strong> — where a coach or club official claims expenses, the account name, sort code and account number to reimburse them, plus receipt images</li>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Device identifiers</strong> — a push-notification token and an app installation identifier, used only to deliver club notifications to your device</li>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Consent records</strong> — a record of the consents you have given (consent to play, health-data consent, social-media consent, code-of-conduct agreement), including the version of the wording you agreed to</li>
    </ul>

    <h3>Children's Information</h3>
    <p>
        Information about child players — including their health information and photographs — is only
        collected from a parent, carer or authorised club official, never from the child directly.
        Registering a player requires confirmation that you have permission from the player, or are able
        to give permission on their behalf if they are under 16. Health information is only collected
        after you have read and accepted a specific health-data disclosure, and you can withdraw that
        consent at any time. Photograph and social-media consents are optional and separate.
    </p>

    <h3>How We Get the Personal Information and Why We Have It</h3>
    <p>Most of the personal information we process is provided to us directly by you for one of the following reasons:</p>
    <ul>
        <li><span className="material-symbols-outlined">task_alt</span>Maintaining player records and registrations</li>
        <li><span className="material-symbols-outlined">task_alt</span>Maintaining subscriptions and processing club fees, tickets and expenses</li>
        <li><span className="material-symbols-outlined">task_alt</span>Health and safety, including:
            <ul>
                <li><span className="material-symbols-outlined">task_alt</span>Medical history, medication and dietary requirements, so coaches and club administrators can keep players safe</li>
                <li><span className="material-symbols-outlined">task_alt</span>Statutory accident and injury reporting (BI510/RIDDOR)</li>
            </ul>
        </li>
        <li><span className="material-symbols-outlined">task_alt</span>Sending you club notifications you have enabled</li>
    </ul>

    <h3>Information Received Indirectly</h3>
    <p>We also receive personal information indirectly, from the following sources in the following scenarios:</p>
    <ul>
        <li><span className="material-symbols-outlined">task_alt</span>The FA</li>
        <li><span className="material-symbols-outlined">task_alt</span>Affiliated Football Leagues</li>
        <li><span className="material-symbols-outlined">task_alt</span>Our secure sign-in service, when you create or use your club account</li>
    </ul>

    <h3>Use of Personal Information</h3>
    <p>We use the information that you have given us to:</p>
    <ul>
        <li><span className="material-symbols-outlined">task_alt</span>Maintain records for each player</li>
        <li><span className="material-symbols-outlined">task_alt</span>Maintain subscription status and process club payments</li>
        <li><span className="material-symbols-outlined">task_alt</span>Provide medical information to coaches, club welfare officers and medical professionals where required to keep players safe</li>
        <li><span className="material-symbols-outlined">task_alt</span>Keep statutory accident records</li>
        <li><span className="material-symbols-outlined">task_alt</span>Reimburse volunteer expenses</li>
        <li><span className="material-symbols-outlined">task_alt</span>Deliver club news, events, polls and notifications</li>
    </ul>

    <h3>Sharing Personal Information</h3>
    <p>We may share this information with:</p>
    <ul>
        <li><span className="material-symbols-outlined">task_alt</span>Your club's authorised officials — access to player health information is restricted to coach, welfare and administrator roles, and access is logged</li>
        <li><span className="material-symbols-outlined">task_alt</span>The FA</li>
        <li><span className="material-symbols-outlined">task_alt</span>Affiliated Football Leagues</li>
        <li><span className="material-symbols-outlined">task_alt</span>Medical Professionals, where needed in an emergency</li>
        <li><span className="material-symbols-outlined">task_alt</span>Stripe, who process payments on our behalf</li>
        <li><span className="material-symbols-outlined">task_alt</span>Google (Firebase Cloud Messaging), solely to deliver push notifications to your device</li>
        <li><span className="material-symbols-outlined">task_alt</span>Our hosting and authentication service providers, who process data on our instructions</li>
    </ul>
    <p>We do not sell personal information, and we do not use it for advertising or third-party analytics.</p>

    <h3>Lawful Bases for Processing</h3>
    <p>Under the UK General Data Protection Regulation (UK GDPR), the lawful bases we rely on for processing this information are:</p>
    <ul>
        <li><span className="material-symbols-outlined">task_alt</span>(a) Your consent. You are able to remove your consent at any time. You can do this by contacting <a href="mailto:privacy@grassroots360.co.uk">privacy@grassroots360.co.uk</a></li>
        <li><span className="material-symbols-outlined">task_alt</span>(b) We have a contractual obligation.</li>
        <li><span className="material-symbols-outlined">task_alt</span>(c) We have a legal obligation.</li>
        <li><span className="material-symbols-outlined">task_alt</span>(d) We have a vital interest.</li>
        <li><span className="material-symbols-outlined">task_alt</span>(e) We need it to perform a public task.</li>
        <li><span className="material-symbols-outlined">task_alt</span>(f) We have a legitimate interest.</li>
    </ul>
    <p>
        For health information, which is special category data, we rely on your explicit consent
        (UK GDPR Article 9(2)(a)), and in a medical emergency on the protection of vital interests
        (Article 9(2)(c)). For accident records we also rely on our legal obligations under health
        and safety law.
    </p>

    <h3>How We Store Your Personal Information</h3>
    <p>
        Your information is securely stored. All information is encrypted in transit (HTTPS), and
        health information is only visible to your club's authorised coach, welfare and administrator
        roles.
    </p>
    <p>
        We keep player information and medical records for 3 months following a player leaving the club,
        after which we dispose of the information by cleansing all systems of personal information
        pertaining to the subject. Statutory accident and injury records are kept for 3 years from the
        date of the record, as required for accident-reporting purposes, and are then disposed of in the
        same way.
    </p>

    <h3>Deleting Your Account and Data</h3>
    <p>You can delete your Grassroots360 account at any time:</p>
    <ul>
        <li><span className="material-symbols-outlined">task_alt</span>In the mobile app: <strong>Settings &gt; Delete Account</strong></li>
        <li><span className="material-symbols-outlined">task_alt</span>Online: use our <Link to="/delete-account">account deletion request form</Link></li>
        <li><span className="material-symbols-outlined">task_alt</span>By email: <a href="mailto:privacy@grassroots360.co.uk">privacy@grassroots360.co.uk</a></li>
    </ul>
    <p>
        Deleting your account removes your sign-in and the personal information held about you, subject
        to records we are legally required to retain (such as statutory accident records) for their
        retention period.
    </p>

    <h3>Your Data Protection Rights</h3>
    <p>Under data protection law, you have rights including:</p>
    <ul>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Your right of access</strong> – You have the right to ask us for copies of your personal information.</li>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Your right to rectification</strong> – You have the right to ask us to rectify information you think is inaccurate or incomplete.</li>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Your right to erasure</strong> – You have the right to ask us to erase your personal information in certain circumstances.</li>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Your right to restriction of processing</strong> – You have the right to ask us to restrict the processing of your personal information in certain circumstances.</li>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Your right to object to processing</strong> – You have the right to object to the processing of your personal information in certain circumstances.</li>
        <li><span className="material-symbols-outlined">task_alt</span><strong>Your right to data portability</strong> – You have the right to ask that we transfer the information you gave us to another organisation, or to you, in certain circumstances.</li>
    </ul>
    <p>
        You are not required to pay any charge for exercising your rights. If you make a request, we have one month to respond to you.
    </p>
    <p>Please contact us at <a href="mailto:privacy@grassroots360.co.uk">privacy@grassroots360.co.uk</a> if you wish to make a request.</p>

    <h3>How to Complain</h3>
    <p>
        If you have any concerns about our use of your personal information, you can make a complaint to us at
        <a href="mailto:privacy@grassroots360.co.uk">privacy@grassroots360.co.uk</a>.
    </p>
    <p>You can also complain to the ICO if you are unhappy with how we have used your data:</p>
    <address>
        <h4>Information Commissioner’s Office</h4>
        Wycliffe House<br/>
        Water Lane<br/>
        Wilmslow<br/>
        Cheshire<br/>
        SK9 5AF<br/>
        Helpline number: <a href="tel:03031231113">0303 123 1113</a><br/>
        Website: <a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer">https://www.ico.org.uk</a>
    </address>
            </div>
        </article>
    )
}

export default Privacy;
